import {
  flow,
  get,
  identity,
  ifThen,
  isFilled,
  log,
  logGroupEnd,
  logGroupStart,
  map,
  pipe,
  set,
  tap,
} from '@logistics-frontend/utils'
import {type Point, SuggestAction} from '@yandex-taxi/js-integration-api'

import type {Order as FormValues, PointAddress} from '../../types'
import {fromSuggestToPointAddress, getCoordinates} from '../../utils'
import {fetchAddressSuggest} from '../deprecated/fetch-address-suggest'

type AddressSuggestResponsePromise = ReturnType<typeof fetchAddressSuggest>
type AddressSuggestResponse = Awaited<AddressSuggestResponsePromise>

const toPointAddress: (response: AddressSuggestResponse) => PointAddress | undefined = //
  flow(
    get('suggest[0].__origin'), //
    ifThen(isFilled, fromSuggestToPointAddress),
  )

export function fetchInitialRoute(from: string, to?: string) {
  const promises: AddressSuggestResponsePromise[] = []
  const sourcePoint = getCoordinates(from) as Point | undefined
  const destinationPoint = getCoordinates(to) as Point | undefined

  if (sourcePoint) {
    promises.push(
      fetchAddressSuggest({
        point: sourcePoint,
        type: 'a',
        location: sourcePoint,
        action: SuggestAction.PIN_DROP,
      }),
    )

    if (destinationPoint) {
      promises.push(
        fetchAddressSuggest({
          point: destinationPoint,
          type: 'b',
          location: sourcePoint,
          action: SuggestAction.PIN_DROP,
        }),
      )
    }
  }

  // если, по итогу, в бек не пошли, то просто вернем начальный стейт
  if (promises.length === 0) {
    return identity as (formValues: FormValues) => FormValues
  }

  // иначе будем обновлять стейт
  return async function _fetchInitialRoute(formValues: FormValues): Promise<FormValues> {
    const [source, destination] = await Promise.allSettled(promises) //
      // не хочу заморачиваться с разными типами PromiseSettledResult, просто скастуем в возвращаемый тип саджеста
      .then(map(get('value')) as unknown as () => AddressSuggestResponse[])
      .then(map(toPointAddress))

    const setSourceAddress: (formValues: FormValues) => FormValues = source
      ? flow(
          tap(logGroupStart('source')),
          tap(() => log('address          ')(source)),
          tap(log('formValues before')),
          set('sourcePoint.address', source),
          tap(log('formValues after ')),
          tap(logGroupEnd()),
        )
      : identity
    const setDestinationAddress: (formValues: FormValues) => FormValues = destination
      ? flow(
          tap(logGroupStart('destination')),
          tap(() => log('address          ')(destination)),
          tap(log('formValues before')),
          set('destinationPoints[0].address', destination),
          tap(log('formValues after ')),
          tap(logGroupEnd()),
        )
      : identity

    return pipe(
      formValues,
      tap(logGroupStart('fetchInitialRoute', true)),
      setSourceAddress,
      setDestinationAddress,
      tap(logGroupEnd()),
    )
  }
}
